/* eslint-disable max-len */
import React, { lazy, Suspense, useContext } from "react";
import { useSelector } from "react-redux";
import cx from "classnames";
import { useMediaQuery } from "@mui/system";
import PropTypes from "prop-types";

import { HeaderTitle } from "../../../components/HeaderTitle";
import { Notifications } from "../../../components/Notification";
import { ApplicationStatus } from "../../../components/ApplicationStatus/ApplicationStatus";

import { agentFormStepper, detailedAccountRoutes, ERROR_MESSAGES } from "../../../constants";
import { LayoutContext } from "../LayoutProvider";

import routes from "../../../routes";
import { StyledFormLayoutContainer, StyledAgentFormNavigation } from "./styled"; //StyledFormLayout
import { checkLoginStatus } from "../../../store/selectors/loginSelector";
import { FormNavigationAgentStep } from "../../../components/FormNavigationAgentStep";
import { FormNavigationContext } from "../../../components/FormNavigation/FormNavigationProvider";
import HeaderProgressBar from "../../../components/ProgressBar/ProgressBarContainer";

const Chat = lazy(() => import("../../../containers/WebChat/Chat"));

export const FormLayoutComponent = ({
  screeningError,
  errorCode,
  errorIcon,
  pathname,
  children
}) => {
  const isAgent = useSelector(checkLoginStatus);
  const isMobile = useMediaQuery("(max-width: 768px)") || window.innerWidth <= 768;
  const [
    isDisplayHeader = false,
    isDisplayScreeningError = false
    // hasVerticalPagination = false
  ] = useContext(LayoutContext);
  const navContext = useContext(FormNavigationContext) || [];
  const [, isChatVisible] = navContext;

  const activeStep =
    agentFormStepper?.find(step =>
      [step.path, ...(step.relatedPath ?? "")].some(path => pathname === path)
    ) || [];

  const activeStepIndex = (activeStep || {}).step;

  const showContentCentered = [
    "/VerifyMobileOTP",
    "/agent/Login",
    "/VerifyEmailOTP",
    "/ComeBackLogin",
    "/ComeBackVerification",
    "/partnerOTPVerification",
    "/efrOTPVerification",
    "/roInviteEFRSuccess",
    "/DulConsentInfo"
  ].some(path => pathname.toLowerCase().includes(path.toLowerCase()));

  const showAgentNavigation =
    pathname === routes.inviteCustomer || pathname === routes.searchProspect;

  const normalizedPathname = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const isDetailedAccountRoute = detailedAccountRoutes.includes(normalizedPathname);

  return pathname === routes.quickapplyLanding ||
    pathname === "/" ||
    pathname === `${routes.quickapplyLanding}/` ||
    isDetailedAccountRoute ? (
    <>{children}</>
  ) : (
    <StyledFormLayoutContainer maxWidth="md">
      <div
        className={cx(
          "formWrapper",
          showContentCentered && "centeredContent",
          isAgent && "agentFormWrapper"
        )}
      >
        <Notifications />
        {isMobile && <HeaderProgressBar />}
        <div className={cx("contentWrapper", isAgent && "showFormNavigation")}>
          {isAgent && showAgentNavigation && (
            <StyledAgentFormNavigation>
              {agentFormStepper.map(step => (
                <FormNavigationAgentStep
                  path={step.path}
                  key={step.step}
                  title={step.title}
                  activeStep={activeStepIndex === step.step}
                  extraClass="agentFormNavigationLink"
                />
              ))}
            </StyledAgentFormNavigation>
          )}
          {isDisplayHeader && <HeaderTitle withoutMarginBottom />}
          {screeningError.error && isDisplayScreeningError ? (
            <ApplicationStatus {...screeningError} />
          ) : errorCode ? (
            <ApplicationStatus icon={errorIcon} text={ERROR_MESSAGES[errorCode]} />
          ) : (
            children
          )}
        </div>
      </div>
      {isChatVisible && (
        <div className={"chatButton"}>
          <Suspense fallback={<div>Loading...</div>}>
            <Chat />
          </Suspense>
        </div>
      )}
    </StyledFormLayoutContainer>
  );
};

FormLayoutComponent.propTypes = {
  screeningError: PropTypes.shape({
    error: PropTypes.bool,
    icon: PropTypes.node,
    text: PropTypes.string
  }),
  errorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  errorIcon: PropTypes.node,
  pathname: PropTypes.string.isRequired,
  children: PropTypes.node
};

FormLayoutComponent.defaultProps = {
  screeningError: { error: false },
  errorCode: null,
  errorIcon: null,
  children: null
};
